import {
  DataFilters,
  FileQueryFilter,
  FilterLogic,
  MetadataFilter,
  OutputFilters,
  PipelineFilter,
  RunFilters,
} from "@via/pages/sample-trackers/samples/types/filters.ts";
import { getRunStatusText } from "@via/pages/projects/runs/utils/helpers.tsx";

/**
 * Maps UI operator values to API operator format
 * Handles special cases like regex patterns for text matching
 */
export function mapOperatorToApiOperator(
  operator: string,
  value: string | number | boolean | Date
): { operator: string; value: any } {
  const stringValue = String(value);

  switch (operator) {
    case "contains":
      // Case-insensitive regex for contains
      return { operator: "regex", value: escapeRegex(stringValue) };
    case "startsWith":
      // Case-insensitive regex for starts with
      return { operator: "regex", value: `^${escapeRegex(stringValue)}` };
    case "endsWith":
      // Case-insensitive regex for ends with
      return { operator: "regex", value: `${escapeRegex(stringValue)}$` };
    case "eq":
    case "ne":
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      // Direct mapping for comparison operators
      return { operator, value };
    default:
      // Default to equals
      return { operator: "eq", value };
  }
}

/**
 * Escapes special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Converts UI filter format to API dataFilters format
 * @param filters Array of metadata filters
 * @param logic Logical operator to combine filters (AND/OR)
 * @returns DataFilters object in API format
 */
export function convertFiltersToDataFilters(
  filters: MetadataFilter[],
  logic: FilterLogic = "OR"
): DataFilters | undefined {
  if (!filters || filters.length === 0) {
    return undefined;
  }

  // Single filter - simple format
  if (filters.length === 1) {
    const filter = filters[0];
    const { operator, value } = mapOperatorToApiOperator(
      filter.operator,
      filter.value
    );
    return {
      [filter.field]: {
        [operator]: value,
      },
    };
  }

  // Multiple filters - use logical operator
  const filterArray = filters.map(filter => {
    const { operator, value } = mapOperatorToApiOperator(
      filter.operator,
      filter.value
    );
    return {
      [filter.field]: {
        [operator]: value,
      },
    };
  });

  return {
    [logic.toLowerCase()]: filterArray,
  };
}

/**
 * Formats a filter for display in a tag
 * @param filter The filter to format
 * @returns Formatted string like "age gt 25"
 */
export function formatFilterForDisplay(filter: MetadataFilter): string {
  const operatorLabel = getOperatorLabel(filter.operator);
  const valueDisplay =
    typeof filter.value === "string" ? filter.value : String(filter.value);
  return `${filter.field} ${operatorLabel} ${valueDisplay}`;
}

/**
 * Gets the display label for an operator
 */
function getOperatorLabel(operator: string): string {
  const labelMap: Record<string, string> = {
    eq: "eq",
    ne: "ne",
    gt: "gt",
    gte: "gte",
    lt: "lt",
    lte: "lte",
    contains: "contains",
    startsWith: "starts with",
    endsWith: "ends with",
    like: "contains",
  };
  return labelMap[operator] || operator;
}

/**
 * Converts UI Pipeline filter format to API runFilters format
 * @param filters Array of pipeline filters
 * @param logic Logical operator to combine filters (AND/OR)
 * @returns RunFilters object in API format
 */
export function convertFiltersToRunFilters(
  filters: PipelineFilter[],
  logic: FilterLogic = "OR"
): RunFilters | undefined {
  if (!filters || filters.length === 0) {
    return undefined;
  }

  // Map filters to API format
  const filterArray = filters.map(filter => {
    const value = filter.value;
    return {
      [filter.field]: {
        [filter.operator]: value,
      },
    };
  });

  // Always use logical operator structure (and/or)
  return {
    [logic.toLowerCase()]: filterArray,
  };
}

/**
 * Formats a Pipeline filter for display in a tag
 * @param filter The filter to format
 * @returns Formatted string like "pipelineName contains RNA"
 *
 * @example
 * formatPipelineFilterForDisplay({
 *   field: "status",
 *   operator: "eq",
 *   value: "Failed|LaunchFailed",
 * });
 * // "status eq Run Error"
 */
export function formatPipelineFilterForDisplay(filter: PipelineFilter): string {
  const operatorLabel = getOperatorLabel(filter.operator);
  const fieldDisplay = filter.field;

  if (filter.field === "status" && typeof filter.value === "string") {
    const rawValues = filter.value
      .split("|")
      .map(s => s.trim())
      .filter(Boolean);
    const labels = Array.from(
      new Set(
        rawValues.map(v => {
          const t = getRunStatusText(v);
          return typeof t === "string" && t.trim() !== "" ? t : v;
        })
      )
    );
    const valueDisplay = labels.join(" / ");
    return `${fieldDisplay} ${operatorLabel} ${valueDisplay}`;
  }

  const valueDisplay = String(filter.value);
  return `${fieldDisplay} ${operatorLabel} ${valueDisplay}`;
}

/**
 * Converts UI Report Query filter format to API outputFilters format
 * @param filters Array of file query filters
 * @param logic Logical operator to combine filters (AND/OR)
 * @returns OutputFilters object in API format
 */
export function convertFileQueryFiltersToOutputFilters(
  filters: FileQueryFilter[],
  logic: FilterLogic = "OR"
): OutputFilters | undefined {
  if (!filters || filters.length === 0) {
    return undefined;
  }

  // Map filters to API format
  const filterArray = filters.map(filter => {
    const baseFilter: any = {
      reportRelativePath: filter.reportRelativePath,
      fields: filter.fields,
    };

    // Add filter conditions if they exist
    if (filter.filter) {
      const filterConditions = filter.filter.and || filter.filter.or || [];
      if (filterConditions.length > 0) {
        const filterLogic = filter.filter.and ? "and" : "or";
        // Filter out conditions with empty field names
        const validConditions = filterConditions
          .map(condition => {
            const fieldName = Object.keys(condition)[0];
            // Skip conditions with empty field names
            if (!fieldName || String(fieldName).trim() === "") {
              return null;
            }
            const operatorObj = condition[fieldName];
            const operator = Object.keys(operatorObj)[0];
            const value = operatorObj[operator];

            // Use mapOperatorToApiOperator for operator conversion
            const { operator: apiOperator, value: apiValue } =
              mapOperatorToApiOperator(operator, value);

            return {
              [fieldName]: {
                [apiOperator]: apiValue,
              },
            };
          })
          .filter(condition => condition !== null);

        if (validConditions.length > 0) {
          baseFilter.filter = {
            [filterLogic]: validConditions,
          };
        }
      }
    }

    return baseFilter;
  });

  // Always use logical operator structure (and/or)
  return {
    [logic.toLowerCase()]: filterArray,
  };
}

/**
 * Formats a Report Query filter for display in a tag
 * @param filter The filter to format
 * @returns Formatted string like "row_headers/file.tsv (Column 1: avg, Column 2: max)"
 */
export function formatFileQueryFilterForDisplay(
  filter: FileQueryFilter
): string {
  const fieldNames = Object.keys(filter.fields);
  const fieldDisplay = fieldNames
    .map(fieldName => `${fieldName}: ${filter.fields[fieldName].aggregation}`)
    .join(", ");
  const fileName =
    filter.reportRelativePath.split("/").pop() || filter.reportRelativePath;

  // Add filter information if present
  let filterDisplay = "";
  if (filter.filter) {
    const filterConditions = filter.filter.and || filter.filter.or || [];
    if (filterConditions.length > 0) {
      const filterLogic = filter.filter.and ? "AND" : "OR";
      const conditionStrings = filterConditions.map(condition => {
        const fieldName = Object.keys(condition)[0];
        const operatorObj = condition[fieldName];
        const operator = Object.keys(operatorObj)[0];
        const value = operatorObj[operator];
        const operatorLabel = getOperatorLabel(operator);
        return `${fieldName} ${operatorLabel} ${value}`;
      });
      filterDisplay = ` [filter: ${conditionStrings.join(` ${filterLogic} `)}]`;
    }
  }

  return `${fileName} (${fieldDisplay})${filterDisplay}`;
}
